import { useQueryClient } from '@tanstack/react-query';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonCard, GiveBoxEvent } from '@bonusly/common-give-box';
import { useBreakpointValue, useDisclosure } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';

import { Bonus, CachedBonuses } from '../../../../../types/bonus';
import { WrappedPresent } from '../../../../seasonal/winter/wrapped-present';
import { removeBonusFromFeed } from '../../cache/mutators/bonus';
import { BonusContent } from '../../components/bonus/bonus-content';
import { EditModal } from '../../components/bonus/edit-givebox-modal';
import { GiftWrappedBonus } from '../../components/bonus/gift-wrapped-bonus';
import { useDeleteBonus } from '../../components/bonus/hooks/use-delete-bonus';
import { ConfirmationModal } from '../../components/common/modal';
import { QueryKey } from '../../hooks/types';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import { useToast } from '../../hooks/use-toast';

export interface BonusCardProps {
	bonus: Bonus;
	feedFilter?: string;
}

export const BonusCard: FC<BonusCardProps> = ({ bonus, feedFilter }) => {
	const trackDelete = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusDelete);
	const { t } = useTranslation(['recognition', 'common']);
	const { createSuccessToast } = useToast();
	const queryClient = useQueryClient();
	const dispatch = useDispatch();
	const { isUserReceiver } = useContextualizedBonusDetails(bonus);

	const editBonusModal = useDisclosure();
	const confirmCloseEditModal = useDisclosure();
	const confirmDeleteModal = useDisclosure();

	const deleteBonus = useDeleteBonus({
		onSuccess: (_data, { id }) => {
			queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => removeBonusFromFeed(id, cached));
			dispatch(GiveBoxEvent.Deleted);

			createSuccessToast(t('delete_bonus.feedback.success', { ns: 'recognition' }));
			confirmDeleteModal.onClose();
		},
	});

	const handleConfirmCloseEditModal = () => {
		editBonusModal.onClose();
		confirmCloseEditModal.onClose();
	};

	const handleClickDelete = () => confirmDeleteModal.onOpen();

	const handleConfirmDelete = () => {
		trackDelete();
		deleteBonus.mutate({ id: bonus.id });
	};
	const variant = useBreakpointValue<any>({ base: 'flat', xs: 'full' });

	return (
		<Fragment>
			<GiftWrappedBonus bonus={bonus} isUserReceiver={isUserReceiver}>
				<WrappedPresent bonusId={bonus.id} bonusCreatedAt={bonus.created_at} isUserReceiver={isUserReceiver}>
					<CommonCard
						boxShadow="none"
						data-testid={`bonus-${bonus.id}`}
						p={0}
						variant={variant}
						borderRadius={'lg'}
						border={'1px solid'}
						borderColor={'border.default'}
					>
						<BonusContent bonus={bonus} feedFilter={feedFilter} handleClickDelete={handleClickDelete} editBonusModal={editBonusModal} />
					</CommonCard>
				</WrappedPresent>
			</GiftWrappedBonus>

			<EditModal
				bonus={bonus}
				isOpen={editBonusModal.isOpen}
				onClose={editBonusModal.onClose}
				onDelete={handleClickDelete}
				onRequestClose={confirmCloseEditModal.onOpen}
			/>

			<ConfirmationModal
				blockScrollOnMount={false}
				confirmText={t('discard', { ns: 'common' })}
				description={t('edit_bonus.confirm_cancel.description', {
					ns: 'recognition',
				})}
				header={t('edit_bonus.confirm_cancel.header', { ns: 'recognition' })}
				onConfirm={handleConfirmCloseEditModal}
				{...confirmCloseEditModal}
			/>

			<ConfirmationModal
				confirmColorScheme="danger"
				confirmText={t('delete_bonus.confirm_delete.text', {
					ns: 'recognition',
				})}
				description={t('delete_bonus.confirm_delete.description', {
					ns: 'recognition',
				})}
				header={t('delete_bonus.confirm_delete.header', {
					ns: 'recognition',
				})}
				isLoading={deleteBonus.isLoading}
				onConfirm={handleConfirmDelete}
				{...confirmDeleteModal}
			/>
		</Fragment>
	);
};
